import { memo, useCallback } from "react";
import MoonLoader from "react-spinners/MoonLoader";
import Swal from "sweetalert2";
import CRMCreateForm from "./CRMCreateForm";
import CRMContactView from "./CRMContactView";
import { useCRMContact } from "../../hooks";
import { useServiceCenter } from "../../contexts";
import "./CRMTab.css";

/**
 * Aba CRM no ContactPanel — orquestra loading/create/view
 */
const CRMTab = memo(({ conversation, contact }) => {
  const { updateConversation } = useServiceCenter();
  const phone = conversation?.phone_raw || conversation?.phone || contact?.phone || "";
  const name = contact?.name || conversation?.contact_name || conversation?.name || "";
  const email = contact?.email || "";
  const contactId = conversation?.contact_id || null;

  const {
    crmContact,
    pipelines,
    columns,
    isLoading,
    isCreating,
    isUpdating,
    error,
    lookupContact,
    createCRMContact,
    updateCRMContact,
    loadColumns,
  } = useCRMContact(contactId, phone);

  const handleUpdate = useCallback(async (data) => {
    const result = await updateCRMContact(data);
    if (result && conversation?.id) {
      const updates = { id: conversation.id };
      if (data.name) updates.name = data.name;
      if ("temperature" in data) updates.temperature = data.temperature;
      if ("priority" in data) updates.priority = data.priority;
      if ("deal_value" in data) updates.deal_value = data.deal_value;
      if (Object.keys(updates).length > 1) {
        updateConversation(updates);
      }
    }
    return result;
  }, [updateCRMContact, updateConversation, conversation?.id]);

  const handleCreate = useCallback(async (data) => {
    try {
      await createCRMContact(data);
      if (data.name && conversation?.id) {
        updateConversation({ id: conversation.id, name: data.name });
      }
      Swal.fire({
        icon: "success",
        title: "Contato adicionado ao CRM",
        text: "Agora você pode acompanhar as negociações",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível criar o contato no CRM",
      });
    }
  }, [createCRMContact, updateConversation, conversation?.id]);

  // Loading
  if (isLoading) {
    return (
      <div className="sc-crm-tab sc-crm-tab--loading">
        <MoonLoader size={24} color="#3b82f6" />
        <span>Verificando CRM...</span>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="sc-crm-tab">
        <div className="sc-crm-tab__error">
          <span className="sc-crm-tab__error-icon">⚠️</span>
          <span className="sc-crm-tab__error-text">{error}</span>
          <button className="sc-crm-tab__retry-btn" onClick={lookupContact}>
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  // Create form
  if (!crmContact) {
    return (
      <div className="sc-crm-tab">
        <CRMCreateForm
          name={name}
          phone={phone}
          email={email}
          pipelines={pipelines}
          columns={columns}
          isCreating={isCreating}
          onLoadColumns={loadColumns}
          onSubmit={handleCreate}
        />
      </div>
    );
  }

  // Contact view
  return (
    <div className="sc-crm-tab">
      <CRMContactView
        contact={crmContact}
        pipelines={pipelines}
        isUpdating={isUpdating}
        onUpdate={handleUpdate}
      />
    </div>
  );
});

CRMTab.displayName = "CRMTab";

export default CRMTab;
